import { Globe, Link2, Lock, type LucideIcon } from "lucide-react";
import type { PlaylistVisibility } from "@gerama/types";
import { cn } from "@gerama/utils";

export const VISIBILITY: Record<
  PlaylistVisibility,
  { label: string; hint: string; icon: LucideIcon }
> = {
  private: { label: "خصوصی", hint: "فقط خودت می‌بینی", icon: Lock },
  public: { label: "عمومی", hint: "در پروفایلت برای همه نمایش داده می‌شود", icon: Globe },
  shared_link: { label: "با لینک", hint: "هر کس لینک را داشته باشد می‌بیند", icon: Link2 },
};

/** Small pill saying who can see a playlist. */
export function VisibilityBadge({
  visibility,
  className,
}: {
  visibility: PlaylistVisibility;
  className?: string;
}) {
  const { label, icon: Icon } = VISIBILITY[visibility];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
        visibility === "private"
          ? "bg-foreground/10 text-muted-foreground"
          : "bg-primary/15 text-primary",
        className,
      )}
    >
      <Icon className="size-3" />
      {label}
    </span>
  );
}

/** Segmented choice between the visibilities offered (create offers private/public only). */
export function VisibilityPicker({
  value,
  onChange,
  options = ["private", "public", "shared_link"],
  disabled,
}: {
  value: PlaylistVisibility;
  onChange: (value: PlaylistVisibility) => void;
  options?: PlaylistVisibility[];
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="چه کسی می‌بیند" className="grid gap-2">
      {options.map((option) => {
        const { label, hint, icon: Icon } = VISIBILITY[option];
        const active = option === value;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(option)}
            className={cn(
              "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-start transition-colors disabled:opacity-60",
              active ? "border-primary bg-primary/10" : "border-border hover:bg-accent",
            )}
          >
            <Icon
              className={cn("size-5 shrink-0", active ? "text-primary" : "text-muted-foreground")}
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{label}</span>
              <span className="text-muted-foreground block text-xs">{hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
